import styles from "./style.module.scss";
import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Input } from "reactstrap";

type Suggestion = {
  id: string;
  codigo: string;
  descripcion?: string;
};

type TypeaheadProps = {
  placeholder?: string;
  minChars?: number;
  debounceMs?: number;
  fetchSuggestions: (q: string) => Promise<Suggestion[]>;
  onSelect: (item: Suggestion) => void;
  defaultValue?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
};

// ✅ Interfaz de los métodos públicos del ref
export type TypeaheadHandle = {
  clear: () => void;
  focus: () => void;
  setValue: (val: string) => void;
};

export const Typeahead = forwardRef<TypeaheadHandle, TypeaheadProps>(
  (
    {
      placeholder = "Código de producto…",
      minChars = 1,
      debounceMs = 250,
      fetchSuggestions,
      onSelect,
      defaultValue = "",
      inputRef,
      onInputChange,
      onInputKeyDown,
    },
    ref
  ) => {
    const [query, setQuery] = useState(defaultValue);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<Suggestion[]>([]);
    const [highlight, setHighlight] = useState<number>(-1);

    const localInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);
    const timer = useRef<number | undefined>(undefined);
    const lastReqId = useRef(0);

    // 🔹 Expone métodos al padre
    useImperativeHandle(ref, () => ({
      clear() {
        setQuery("");
        setItems([]);
        setOpen(false);
        setHighlight(-1);
      },
      focus() {
        localInputRef.current?.focus();
      },
      setValue(val: string) {
        setQuery(val);
      },
    }));

    // 🔹 Asignar refs internos y externos
    const setInputRefs = (el: HTMLInputElement | null) => {
      localInputRef.current = el;
      if (typeof inputRef === "function") inputRef(el);
      else if (inputRef && typeof inputRef === "object")
        (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
          el;
    };

    // 🔹 Cerrar al click fuera
    useEffect(() => {
      const onDocClick = (e: MouseEvent) => {
        if (!localInputRef.current || !listRef.current) return;
        const target = e.target as Node;
        if (
          !localInputRef.current.contains(target) &&
          !listRef.current.contains(target)
        ) {
          setOpen(false);
          setHighlight(-1);
        }
      };
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    // 🔹 Buscar con debounce
    useEffect(() => {
      if (timer.current) window.clearTimeout(timer.current);
      if (!query || query.length < minChars) {
        setItems([]);
        setOpen(false);
        setLoading(false);
        return;
      }
      setLoading(true);
      timer.current = window.setTimeout(async () => {
        const reqId = ++lastReqId.current;
        try {
          const res = await fetchSuggestions(query);
          if (reqId === lastReqId.current) {
            setItems(res ?? []);
            setOpen((res?.length ?? 0) > 0);
            setHighlight(res.length ? 0 : -1);
          }
        } finally {
          if (reqId === lastReqId.current) setLoading(false);
        }
      }, debounceMs);
      return () => {
        if (timer.current) window.clearTimeout(timer.current);
      };
    }, [query, minChars, debounceMs, fetchSuggestions]);

    const handleInternalKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
      e
    ) => {
      if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        setOpen(items.length > 0);
        setHighlight(0);
        onInputKeyDown?.(e);
        return;
      }
      if (open) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlight((h) => Math.min(h + 1, items.length - 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === "Enter") {
          e.preventDefault();
          if (highlight >= 0 && items[highlight]) {
            const item = items[highlight];
            onSelect(item);
            setQuery(item.codigo);
            setOpen(false);
          }
        } else if (e.key === "Escape") {
          setOpen(false);
          setHighlight(-1);
        }
      }
      onInputKeyDown?.(e);
    };

    const selectItem = (item: Suggestion) => {
      onSelect(item);
      setQuery(item.codigo);
      setOpen(false);
      localInputRef.current?.focus();
    };

    const renderText = (text: string) => {
      const q = query.trim();
      if (!q) return text;
      const idx = text.toLowerCase().indexOf(q.toLowerCase());
      if (idx === -1) return text;
      return (
        <>
          {text.slice(0, idx)}
          <strong>{text.slice(idx, idx + q.length)}</strong>
          {text.slice(idx + q.length)}
        </>
      );
    };

    return (
      <div style={{ position: "relative" }}>
        <Input
          id="quick-add-code"
          data-testid="quick-add-code"
          type="text"
          autoComplete="off"
          innerRef={setInputRefs}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onInputChange?.(e);
          }}
          onKeyDown={handleInternalKeyDown}
          onFocus={() => items.length && setOpen(true)}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="typeahead-list"
          aria-activedescendant={highlight >= 0 ? `opt-${highlight}` : undefined}
          style={{ width: "100%" }}
        />
        {open && (
          <ul
            id="typeahead-list"
            ref={listRef}
            role="listbox"
            style={{
              position: "absolute",
              zIndex: 10,
              top: "100%",
              left: 0,
              right: 0,
              maxHeight: 280,
              overflowY: "auto",
              margin: 0,
              padding: 0,
              listStyle: "none",
              background: "white",
              border: "1px solid #ddd",
              boxShadow: "0 6px 18px rgba(0,0,0,.06)",
            }}
          >
            {items.map((it, i) => (
              <li
                id={`opt-${i}`}
                key={it.id}
                role="option"
                aria-selected={i === highlight}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectItem(it)}
                style={{
                  padding: "8px 10px",
                  cursor: "pointer",
                  background: i === highlight ? "#f5f7ff" : "transparent",
                  display: "flex",
                  gap: 8,
                  alignItems: "baseline",
                }}
              >
                <span style={{ minWidth: 120, fontFamily: "monospace" }}>
                  {renderText(it.codigo)}
                </span>
                {it.descripcion && (
                  <span
                    style={{
                      color: "#666",
                      fontSize: 12,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {it.descripcion}
                  </span>
                )}
              </li>
            ))}
            {!items.length && !loading && (
              <li style={{ padding: 10, color: "#777" }}>Sin resultados</li>
            )}
          </ul>
        )}
        {loading && <div className={styles.spinerLoader} />}
      </div>
    );
  }
);